import React, { useState, useEffect, useMemo } from 'react';

interface FakeTerminalProps {
  isOpen: boolean;
  isMaximized: boolean;
  onClose: () => void;
  onMaximize: () => void;
  onMinimize: () => void;
  zIndex: number;
  onClick: () => void;
}

/**
 * Animated terminal window that types out code lines with a cursor effect, looping on completion.
 * @param {FakeTerminalProps} props - Standard window props (open, maximize, minimize, close, zIndex, onClick).
 */
const FakeTerminal: React.FC<FakeTerminalProps> = ({ isOpen, isMaximized, onClose, onMaximize, onMinimize, zIndex, onClick }) => {
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [displayedLines, setDisplayedLines] = useState<string[]>([]);

  const codeLines = useMemo(() => [
    "// Desktop.tsx",
    "import React, { useState } from 'react';",
    "import Taskbar from './Taskbar';",
    "import DigitalClock from './DigitalClock';",
    "",
    "const Desktop = () => {",
    "  const [openWindows, setOpenWindows] = useState({",
    "    linkedin: false,",
    "    github: false,",
    "    mail: false",
    "  });",
    "",
    "  return (",
    "    <div className=\"desktop\">",
    "      <DigitalClock />",
    "      <Taskbar onAppClick={handleAppClick} />",
    "    </div>",
    "  );",
    "};",
    "",
    "// Taskbar.tsx",
    "const Taskbar = () => {",
    "  const [currentTime, setCurrentTime] = useState(new Date());",
    "",
    "  useEffect(() => {",
    "    const timer = setInterval(() => {",
    "      setCurrentTime(new Date());",
    "    }, 1000);",
    "    return () => clearInterval(timer);",
    "  }, []);",
    "",
    "  return (",
    "    <div className=\"taskbar\">",
    "      <div className=\"start-button\">⊞</div>",
    "      <div className=\"datetime\">",
    "        {currentTime.toLocaleTimeString()}",
    "      </div>",
    "    </div>",
    "  );",
    "};",
  ], []);

  useEffect(() => {
    if (currentLineIndex >= codeLines.length) {
      // Reset and start over
      setTimeout(() => {
        setCurrentLineIndex(0);
        setCurrentCharIndex(0);
        setDisplayedLines([]);
      }, 3000);
      return;
    }

    const currentLine = codeLines[currentLineIndex];
    
    if (currentCharIndex <= currentLine.length) {
      const timer = setTimeout(() => {
        const newDisplayedLines = [...displayedLines];
        newDisplayedLines[currentLineIndex] = currentLine.substring(0, currentCharIndex);
        setDisplayedLines(newDisplayedLines);
        setCurrentCharIndex(currentCharIndex + 1);
      }, currentLine === "" ? 500 : Math.random() * 50 + 30); // Faster typing, pause on empty lines

      return () => clearTimeout(timer);
    } else {
      // Move to next line
      const timer = setTimeout(() => {
        setCurrentLineIndex(currentLineIndex + 1);
        setCurrentCharIndex(0);
      }, currentLine.startsWith("$") ? 800 : currentLine.startsWith("//") ? 400 : 200);

      return () => clearTimeout(timer);
    }
  }, [currentLineIndex, currentCharIndex, displayedLines, codeLines]);

  const getLineClass = (line: string, index: number) => {
    if (line.startsWith("$")) return "terminal-command";
    if (line.startsWith("//")) return "terminal-comment";
    if (line.includes("import") || line.includes("const") || line.includes("return")) return "terminal-keyword";
    if (line.startsWith("✓")) return "terminal-success";
    if (line.includes("localhost") || line.includes("npm")) return "terminal-info";
    return "terminal-text";
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={`window-overlay terminal ${isMaximized ? 'maximized' : ''}`} style={{ zIndex }}>
      <div className={`window fake-terminal ${isMaximized ? 'maximized' : ''}`} onClick={onClick}>
        <div className="window-titlebar">
          <div className="window-title">
            <span className="window-icon">💻</span>
            Terminal - ~/windows11-portfolio
          </div>
          <div className="window-controls">
            <button className="window-control minimize" onClick={onMinimize}>−</button>
            <button className="window-control maximize" onClick={onMaximize}>
              {isMaximized ? '🗗' : '⬜'}
            </button>
            <button className="window-control close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="window-content">
          <div className="terminal-content">
        {displayedLines.map((line, index) => (
          <div key={index} className={`terminal-line ${getLineClass(line, index)}`}>
            {line}
            {index === currentLineIndex && currentCharIndex <= codeLines[currentLineIndex]?.length && (
              <span className="terminal-cursor">|</span>
            )}
          </div>
        ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FakeTerminal;